import type { ReactNode } from 'react'
import { Dialog } from '@base-ui/react/dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  closable?: boolean
  className?: string
  afterClose?: () => void
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  closable = true,
  className,
  afterClose,
}: ModalProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (next || closable) onOpenChange(next)
      }}
      onOpenChangeComplete={(next) => {
        if (!next) afterClose?.()
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-[1000] bg-slate-950/35" />
        <Dialog.Popup
          className={cn(
            'fixed left-1/2 top-1/2 z-[1001] flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border border-border bg-card text-foreground shadow-xl outline-none',
            className,
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="text-base font-semibold">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-muted-foreground">
                  {description}
                </Dialog.Description>
              )}
            </div>
            {closable && (
              <Dialog.Close
                aria-label="關閉"
                className="rounded p-1 text-muted-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-primary"
              >
                <X size={18} />
              </Dialog.Close>
            )}
          </div>
          <div className="min-h-0 overflow-y-auto p-5">{children}</div>
          {footer != null && (
            <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border px-5 py-4">
              {footer}
            </div>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
